const REPLACEMENT_CHARACTER = '�';

function isControlCharacter(code: number): boolean {
  return code < 0x20 || (code >= 0x7f && code <= 0x9f);
}

/**
 * Cleans one tracking value (a utm_* / lh_* / gclid / adgroup_id value) before it is stored or read back.
 *
 * Control characters are removed and an unpaired UTF-16 surrogate becomes U+FFFD, so the value is always
 * well-formed text that can be percent-encoded for a request header. Everything else, including characters
 * above U+00FF such as the arrow in a Customer.io campaign name, is kept as is: the HTTP client encodes it
 * when it builds the header, and the API decodes it, so the campaign arrives intact (AM-5103).
 * Non-string values are returned unchanged.
 */
export function sanitizeTrackingValue<T>(value: T): T | string {
  if (typeof value !== 'string') {
    return value;
  }

  let result = '';
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = i + 1 < value.length ? value.charCodeAt(i + 1) : 0;
      if (next >= 0xdc00 && next <= 0xdfff) {
        result += value[i] + value[i + 1];
        i++;
      } else {
        result += REPLACEMENT_CHARACTER;
      }
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      result += REPLACEMENT_CHARACTER;
    } else if (!isControlCharacter(code)) {
      result += value[i];
    }
  }
  return result;
}

/** Applies `sanitizeTrackingValue` to every value of a stored tracking data record. */
export function sanitizeTrackingData(data: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    result[key] = sanitizeTrackingValue(value);
  }
  return result;
}
